import React, { useState } from "react";
import { apibaseurl } from "../../../core/webservice/Webservice_url";
import './addressDetailView.css'
type AddressDetailsViewProps = {
  data: any[];
};

const fileUrl = (path?: string | null) => (path ? `${apibaseurl}${path}` : "");

const getInitials = (name?: string | null) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const AddressDetailsView = ({ data }: AddressDetailsViewProps) => {
  const [preview, setPreview] = useState<{ url: string; label: string } | null>(
    null,
  );

  if (!data || data.length === 0) {
    return (
      <div className="card-body text-center py-5">
        <i
          className="ti ti-map-pin-off text-secondary"
          style={{ fontSize: 48 }}
        ></i>
        <h6 className="mt-3 mb-1">No Details Found</h6>
        <p className="text-muted mb-0">
          The candidate has not submitted any Details information yet.
        </p>
      </div>
    );
  }

  return (
    <div className="card-body"> 
      {data.map((address: any) => {
        const photos = [
          {
            key: "live_selfie_url",
            label: "Live Selfie",
            icon: "ti ti-camera",
            url: address.live_selfie_url,
            verified: address.live_selfie_verified,
          },
          {
            key: "id_proof_photo",
            label: "ID Proof",
            icon: "ti ti-id",
            url: address.id_proof_photo,
          },
          {
            key: "address_proof_photo",
            label: "Address Proof",
            icon: "ti ti-file-certificate",
            url: address.address_proof_photo,
          },
          {
            key: "house_photo",
            label: "House Photo",
            icon: "ti ti-home",
            url: address.house_photo,
          },
          {
            key: "locality_photo",
            label: "Locality Photo",
            icon: "ti ti-building-community",
            url: address.locality_photo,
          },
          {
            key: "digital_signature_url",
            label: "Digital Signature",
            icon: "ti ti-signature",
            url: address.digital_signature_url,
          },
        ].filter((p) => p.url);

        const fullAddress = [
          address.address_line1,
          address.address_line2,
          address.address_line3,
        ]
          .filter(Boolean)
          .join(", ");

        const mapsLink =
          address.address_latitude && address.address_longitude
            ? `https://www.google.com/maps?q=${address.address_latitude},${address.address_longitude}`
            : "";

        const coordinates =
          address.address_latitude && address.address_longitude
            ? `${Number(address.address_latitude).toFixed(5)}, ${Number(
                address.address_longitude,
              ).toFixed(5)}`
            : "—";

        const stay = address.period_of_stay
          ? `${address.period_of_stay} ${address.period_of_stay_param || ""}`
          : "—";

        return (
          <div key={address.address_id} className="mb-4">
            <div className="row g-4 mb-4">
              {/* ================= Address Details ================= */}
              <div className="col-lg-8">
                <div className="adv-card">
                  <div className="adv-card-head">
                    <div className="adv-head-icon">
                      <i className="ti ti-map-pin"></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0">Address Details</h6>
                      <small className="text-muted">
                        Location submitted by the candidate
                      </small>
                    </div>
                  </div>

                  <div className="p-3 p-md-4">
                    {/* Full address */}
                    <div className="adv-address-box mb-4">
                      <div className="adv-label">Full Address</div>
                      <div
                        className="fw-bold text-dark"
                        style={{ fontSize: 14, lineHeight: 1.5 }}
                      >
                        {fullAddress || "—"}
                      </div>
                    </div>

                    {/* City / State / Pincode / Country */}
                    <div className="row g-3 mb-4">
                      {[
                        {
                          icon: "ti ti-building",
                          label: "City",
                          value: address.city,
                        },
                        {
                          icon: "ti ti-map",
                          label: "State",
                          value: address.state,
                        },
                        {
                          icon: "ti ti-mailbox",
                          label: "Pincode",
                          value: address.pincode,
                        },
                        {
                          icon: "ti ti-world",
                          label: "Country",
                          value: address.country,
                        },
                      ].map((item) => (
                        <div className="col-md-3 col-6" key={item.label}>
                          <div className="adv-mini">
                            <div className="adv-label">
                              <i
                                className={`${item.icon} me-1`}
                                style={{ color: "#E67E22" }}
                              ></i>
                              {item.label}
                            </div>
                            <div className="adv-value">{item.value || "—"}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Resolved location + coordinates + map */}
                    <div
                      className="d-flex flex-wrap align-items-center justify-content-between gap-3 p-3"
                      style={{
                        borderRadius: 14,
                        background: "#F9FAFB",
                        border: "1px dashed #F4B183",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="adv-stat-icon"
                          style={{ background: "#FFF3E6", color: "#E67E22" }}
                        >
                          <i className="ti ti-current-location"></i>
                        </div>
                        <div>
                          <div className="adv-label mb-0">Resolved Location</div>
                          <div className="adv-value">
                            {address.resolved_address || "—"}
                          </div>
                          <small className="text-muted">
                            <i className="ti ti-map-pin-2 me-1"></i>
                            {coordinates}
                          </small>
                        </div>
                      </div>

                      {mapsLink && (
                        <a
                          href={mapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm px-3"
                        >
                          <i className="ti ti-map-2 me-1"></i>
                          View on Map
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ================= Stay + Verifier ================= */}
              <div className="col-lg-4">
                <div className="d-flex flex-column gap-4 h-100">
                  {/* Stay details */}
                  <div className="adv-card" style={{ height: "auto" }}>
                    <div className="adv-card-head">
                      <div
                        className="adv-head-icon"
                        style={{ background: "#0EA5E9" }}
                      >
                        <i className="ti ti-home-check"></i>
                      </div>
                      <h6 className="fw-bold mb-0">Stay Details</h6>
                    </div>

                    <div className="p-3 d-flex flex-column gap-3">
                      <div className="adv-stat">
                        <div
                          className="adv-stat-icon"
                          style={{ background: "#E0F2FE", color: "#0284C7" }}
                        >
                          <i className="ti ti-calendar-time"></i>
                        </div>
                        <div>
                          <div className="adv-label mb-0">Period of Stay</div>
                          <div className="adv-value" style={{ fontSize: 18 }}>
                            {stay}
                          </div>
                        </div>
                      </div>

                      <div className="adv-stat">
                        <div
                          className="adv-stat-icon"
                          style={{ background: "#DCFCE7", color: "#16A34A" }}
                        >
                          <i className="ti ti-key"></i>
                        </div>
                        <div>
                          <div className="adv-label mb-0">Ownership Status</div>
                          <div className="adv-value" style={{ fontSize: 18 }}>
                            {address.ownership_status || "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Verifier */}
                  <div className="adv-card flex-grow-1">
                    <div className="adv-card-head">
                      <div
                        className="adv-head-icon"
                        style={{ background: "#8B5CF6" }}
                      >
                        <i className="ti ti-user-check"></i>
                      </div>
                      <h6 className="fw-bold mb-0">Verifier</h6>
                    </div>

                    <div className="p-3">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="adv-avatar">
                          {getInitials(address.verifier_full_name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div
                            className="fw-bold text-dark"
                            style={{ fontSize: 17 }}
                          >
                            {address.verifier_full_name || "—"}
                          </div>
                          {address.verifier_relationship && (
                            <span
                              className="badge rounded-pill mt-1"
                              style={{
                                background: "#EDE9FE",
                                color: "#6D28D9",
                                fontWeight: 600,
                              }}
                            >
                              {address.verifier_relationship}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="adv-mini">
                        <div className="adv-label">
                          <i
                            className="ti ti-flag me-1"
                            style={{ color: "#E67E22" }}
                          ></i>
                          Landmark
                        </div>
                        <div className="adv-value">
                          {address.verifier_addrlandmark || "Not provided"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= Photos ================= */}
            <div className="adv-card" style={{ height: "auto" }}>
              <div className="adv-card-head">
                <div
                  className="adv-head-icon"
                  style={{ background: "#EC4899" }}
                >
                  <i className="ti ti-photo"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-0">Selfie & Uploaded Photos</h6>
                  <small className="text-muted">
                    Click any photo to view it larger
                  </small>
                </div>
                <span
                  className="badge rounded-pill px-3 py-2"
                  style={{ background: "#FCE7F3", color: "#BE185D" }}
                >
                  {photos.length} {photos.length === 1 ? "file" : "files"}
                </span>
              </div>

              <div className="p-3 p-md-4">
                {photos.length > 0 ? (
                  <div className="row g-4">
                    {photos.map((photo) => (
                      <div
                        className="col-xl-3 col-lg-4 col-md-6"
                        key={photo.key}
                      >
                        <div
                          className="adv-photo"
                          onClick={() =>
                            setPreview({
                              url: fileUrl(photo.url),
                              label: photo.label,
                            })
                          }
                        >
                          <div className="adv-photo-fallback">
                            <i className={photo.icon}></i>
                          </div>

                          <img
                            src={fileUrl(photo.url)}
                            alt={photo.label}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />

                          <div className="adv-photo-eye">
                            <i className="ti ti-eye"></i>
                          </div>

                          <div className="adv-photo-shade">
                            <div className="d-flex align-items-center gap-2">
                              <i className={photo.icon}></i>
                              <span className="fw-semibold">{photo.label}</span>
                            </div>

                            {photo.key === "live_selfie_url" && (
                              <span
                                className="badge"
                                style={{
                                  background: photo.verified
                                    ? "#22C55E"
                                    : "#EF4444",
                                }}
                              >
                                <i
                                  className={`ti ${
                                    photo.verified ? "ti-circle-check" : "ti-x"
                                  } me-1`}
                                ></i>
                                {photo.verified ? "Verified" : "Not Verified"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="ti ti-photo-off fs-1 d-block mb-2"></i>
                    No photos uploaded.
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* ================= Lightbox ================= */}
      {preview && (
        <div className="adv-lightbox" onClick={() => setPreview(null)}>
          <button
            type="button"
            className="adv-lightbox-close"
            onClick={() => setPreview(null)}
            aria-label="Close preview"
          >
            <i className="ti ti-x"></i>
          </button>

          <img
            src={preview.url}
            alt={preview.label}
            onClick={(e) => e.stopPropagation()}
          />

          <div className="text-white fw-semibold mt-3">{preview.label}</div>
        </div>
      )}
    </div>
  );
};

export default AddressDetailsView; 